const SKIP_NOTIFY_URL_PARTS = ['/checkStatus', '/logs/', '/getDateTime'];

const NOISY_ERROR_SNIPPETS = [
    'ResizeObserver loop',
    'Script error.',
    'chrome-extension://',
    'moz-extension://',
];

const listeners = new Set();

/** Lets axios interceptors raise a toast without importing React components. */
export const subscribeGlobalError = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

export const emitGlobalError = (payload) => {
    listeners.forEach((listener) => listener(payload));
};

export const isCancelledRequest = (error) =>
    error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError';

export const isNetworkError = (error) => {
    if (!error || error.response) return false;
    const code = error.code || '';
    const message = String(error.message || '').toLowerCase();
    return (
        code === 'ERR_NETWORK' ||
        code === 'ECONNABORTED' ||
        code === 'ERR_INTERNET_DISCONNECTED' ||
        message.includes('network error') ||
        message.includes('failed to fetch') ||
        message.includes('timeout')
    );
};

export const shouldSkipApiNotify = (error) => {
    const url = `${error?.config?.url || ''} ${error?.config?.baseURL || ''}`;
    return SKIP_NOTIFY_URL_PARTS.some((part) => url.includes(part));
};

export const shouldIgnoreWindowError = (message) => {
    const text = String(message || '');
    return NOISY_ERROR_SNIPPETS.some((snippet) => text.includes(snippet));
};

/** Returns a user-facing message, or null when the screen should handle it itself. */
export const getApiErrorMessage = (error) => {
    if (isCancelledRequest(error)) return null;
    if (isNetworkError(error)) {
        return 'Unable to reach the server. Please check your connection and try again.';
    }

    const status = error?.response?.status;
    const serverMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.Message;

    if (status >= 500) {
        return serverMessage || 'The server is temporarily unavailable. Please try again shortly.';
    }
    if (status === 404) {
        return serverMessage || 'The requested information could not be found.';
    }
    if (status === 429) {
        return 'Too many requests. Please wait a moment and try again.';
    }
    return null;
};

export const getUnhandledErrorMessage = (reason) => {
    if (!reason || reason.globalNotified || isCancelledRequest(reason)) return null;
    if (isNetworkError(reason)) return getApiErrorMessage(reason);
    if (shouldIgnoreWindowError(reason?.message || reason)) return null;
    return 'Something went wrong. Please try again.';
};
